import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  Input,
  IconButton,
  Select,
  Option,
  Box,
  Typography,
  Card,
  Chip,
  Button,
  Tooltip,
  Textarea,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/joy';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ListIcon from '@mui/icons-material/FormatListBulleted';
import CalendarIcon from '@mui/icons-material/CalendarMonth';
import AbcIcon from '@mui/icons-material/Abc';
import NumbersIcon from '@mui/icons-material/Numbers';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ImageIcon from '@mui/icons-material/Image';

const typeOptions = [
  { value: 'string', label: 'Text', icon: <AbcIcon sx={{ fontSize: 17, color: 'primary.400' }} /> },
  { value: 'integer', label: 'Integer', icon: <NumbersIcon sx={{ fontSize: 17, color: 'primary.400' }} /> },
  { value: 'float', label: 'Decimal', icon: <NumbersIcon sx={{ fontSize: 17, color: 'info.400' }} /> },
  { value: 'date', label: 'Date', icon: <CalendarIcon sx={{ fontSize: 17, color: 'warning.500' }} /> },
  { value: 'list', label: 'List', icon: <ListIcon sx={{ fontSize: 17, color: 'success.500' }} /> },
  { value: 'image', label: 'Image', icon: <ImageIcon sx={{ fontSize: 17, color: 'secondary.500' }} /> },
];

const getTypeObj = (type) => typeOptions.find((opt) => opt.value === type) || typeOptions[0];

const JsonStructureEditor = ({ value, onChange, onClose }) => {
  const [fields, setFields] = useState(value || []);
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const fieldRefs = useRef([]);

  // Sync with parent value
  useEffect(() => {
    setFields(value || []);
    setDirty(false);
    setHistory([]);
    setFuture([]);
  }, [value]);

  // Undo/redo logic
  const pushHistory = (newFields) => {
    setHistory((h) => [...h, fields]);
    setFields(newFields);
    setFuture([]);
    setDirty(true);
  };

  const handleUndo = useCallback(() => {
    if (!history.length) return;
    const prev = history[history.length - 1];
    setFuture((f) => [fields, ...f]);
    setHistory((h) => h.slice(0, h.length - 1));
    setFields(prev);
    setDirty(true);
  }, [fields, history]);

  const handleRedo = useCallback(() => {
    if (!future.length) return;
    const next = future[0];
    setHistory((h) => [...h, fields]);
    setFuture((f) => f.slice(1));
    setFields(next);
    setDirty(true);
  }, [fields, future]);

  useEffect(() => {
    const handleKey = (e) => {
      const tag = e.target.tagName.toLowerCase();
      const isInput = tag === 'input' || tag === 'textarea';
      if (isInput && !(e.metaKey || e.ctrlKey)) return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      if (
        ((e.ctrlKey || e.metaKey) && e.key === 'y') ||
        ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey)
      ) {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleRedo, handleUndo]);

  // Actions
  const handleKeyChange = (index, newKey) => {
    const updatedValue = fields.map((field, i) =>
      index === i ? { ...field, key: newKey } : field
    );
    pushHistory(updatedValue);
  };
  const handleTypeChange = (index, newType) => {
    const updatedValue = fields.map((field, i) =>
      index === i
        ? { ...field, type: newType, value: newType === 'list' ? [] : '' }
        : field
    );
    pushHistory(updatedValue);
  };
  const handleDeleteField = (index) => {
    const updatedValue = fields.filter((_, i) => i !== index);
    pushHistory(updatedValue);
  };
  const handleSubKeyChange = (index, subIndex, newKey) => {
    const updatedValue = fields.map((field, i) =>
      i === index
        ? {
            ...field,
            value: field.value.map((subField, j) =>
              j === subIndex ? { ...subField, key: newKey } : subField
            ),
          }
        : field
    );
    pushHistory(updatedValue);
  };
  const handleSubValueChange = (index, subIndex, newValue) => {
    const updatedValue = fields.map((field, i) =>
      i === index
        ? {
            ...field,
            value: field.value.map((subField, j) =>
              j === subIndex ? { ...subField, value: newValue } : subField
            ),
          }
        : field
    );
    pushHistory(updatedValue);
  };
  const handleDeleteSubField = (index, subIndex) => {
    const updatedValue = fields.map((field, i) =>
      i === index
        ? {
            ...field,
            value: field.value.filter((_, j) => j !== subIndex),
          }
        : field
    );
    pushHistory(updatedValue);
  };
  const handleAddSubField = (index) => {
    const updatedValue = fields.map((field, i) =>
      i === index
        ? {
            ...field,
            value: [...field.value, { key: '', value: '' }],
          }
        : field
    );
    pushHistory(updatedValue);
  };
  const handleValueChange = (index, newValue) => {
    const updatedValue = fields.map((field, i) =>
      index === i ? { ...field, value: newValue } : field
    );
    pushHistory(updatedValue);
  };
  const handleAddField = () => {
    const newFields = [...fields, { key: '', value: '', type: 'string' }];
    pushHistory(newFields);
    setTimeout(() => {
      if (fieldRefs.current[newFields.length - 1]) {
        fieldRefs.current[newFields.length - 1].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // Save handler
  const handleSave = () => {
    onChange?.(fields);
    setDirty(false);
    if (onClose) onClose();
  };

  // Close handler (with dirty check)
  const handleClose = () => {
    if (dirty) setConfirmOpen(true);
    else if (onClose) onClose();
  };

  return (
    <>
      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        minHeight: 500,
        height: '70vh',
        width: '100%',
        gap: 2,
      }}>
        {/* Main Editor */}
        <Box sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          pr: 0.5,
        }}>
          {/* Header/Toolbar */}
          <Box sx={{
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 0.5,
          }}>
            <Box>
              <Typography level="h4" sx={{ fontWeight: 700 }}>JSON Schema Editor</Typography>
              <Typography level="body2" sx={{ color: 'neutral.500', fontSize: 13 }}>
                {fields.length} field{fields.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Undo (Ctrl+Z)">
                <span>
                  <IconButton
                    onClick={handleUndo}
                    disabled={history.length === 0}
                    variant="plain"
                    sx={{
                      border: '1.5px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      bgcolor: 'background.surface',
                      color: 'text.primary',
                      '&:hover': { bgcolor: 'background.level1' }
                    }}>
                    <UndoIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Redo (Ctrl+Y / Ctrl+Shift+Z)">
                <span>
                  <IconButton
                    onClick={handleRedo}
                    disabled={future.length === 0}
                    variant="plain"
                    sx={{
                      border: '1.5px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      bgcolor: 'background.surface',
                      color: 'text.primary',
                      '&:hover': { bgcolor: 'background.level1' }
                    }}>
                    <RedoIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
          {/* Main Fields */}
          <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5, pb: 2 }}>
            {fields.length === 0 && (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pt: 8,
                pb: 10,
                color: 'neutral.400'
              }}>
                <ListIcon sx={{ fontSize: 44, mb: 2, opacity: 0.22 }} />
                <Typography level="h5" sx={{ color: 'inherit' }}>
                  No fields yet
                </Typography>
                <Typography level="body2" sx={{ mt: 1, color: 'inherit' }}>
                  Click "Add Field" to get started
                </Typography>
              </Box>
            )}
            {fields.map((field, index) => {
              const typeObj = getTypeObj(field.type);

              return (
                <Card
                  ref={el => fieldRefs.current[index] = el}
                  key={index}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    mb: 1.2,
                    borderRadius: 'lg',
                    backgroundColor: 'background.surface',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.3, mb: field.type === 'list' ? 0 : 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 34,
                        height: 34,
                        borderRadius: 1,
                        bgcolor: 'background.body',
                        border: '1.5px solid',
                        borderColor: 'neutral.100',
                        color: 'inherit',
                        flexShrink: 0,
                      }}
                    >
                      {typeObj.icon}
                    </Box>
                    <Input
                      variant="outlined"
                      onChange={(e) => handleKeyChange(index, e.target.value)}
                      value={field.key}
                      size="sm"
                      placeholder="Field name"
                      sx={{
                        flex: 3,
                        fontWeight: 600,
                        fontSize: 15,
                        background: 'transparent'
                      }}
                    />
                    <Select
                      variant="outlined"
                      value={field.type || ''}
                      onChange={(_, newType) => handleTypeChange(index, newType)}
                      size="sm"
                      sx={{
                        flex: 2,
                        minWidth: 120,
                        backgroundColor: 'background.level1',
                        borderRadius: 1.5,
                        px: 1,
                        fontWeight: 500,
                        fontSize: 15,
                        boxShadow: 'none',
                        border: '1.5px solid',
                        borderColor: 'divider',
                      }}
                      slotProps={{ button: { sx: { px: 2, fontWeight: 500 } } }}
                    >
                      {typeOptions.map((option) => (
                        <Option key={option.value} value={option.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                            {option.icon} {option.label}
                          </Box>
                        </Option>
                      ))}
                    </Select>
                    <Tooltip title="Delete field">
                      <IconButton
                        onClick={() => handleDeleteField(index)}
                        size="sm"
                        color="neutral"
                        sx={{ ml: 1, '&:hover': { backgroundColor: 'danger.softBg' } }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {/* Field Body */}
                  {field.type === 'list' ? (
                    <Box sx={{
                      mt: 0.7,
                      ml: 2.7,
                      pl: 1.1,
                      borderLeft: '2.2px solid #f2f4f7'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.7, mt: 0.5 }}>
                        <KeyboardArrowDownIcon sx={{ fontSize: 19, color: 'neutral.600', mr: 0.7 }} />
                        <ListIcon sx={{ fontSize: 18, mr: 0.7, color: 'success.400' }} />
                        <Typography level="body2" sx={{ fontWeight: 500, mr: 0.7 }}>
                          Subfields
                        </Typography>
                        <Chip size="sm" variant="soft" sx={{ fontWeight: 500, color: 'neutral.600', backgroundColor: 'background.level1', px: 1.2 }}>
                          {field.value.length}
                        </Chip>
                      </Box>
                      <Box sx={{ mt: 1, mb: 1.5 }}>
                        {Array.isArray(field.value) && field.value.length === 0 && (
                          <Typography level="body2" sx={{ color: 'neutral.400', mb: 1.5, ml: 1 }}>
                            No subfields yet.
                          </Typography>
                        )}
                        {Array.isArray(field.value) && field.value.map((subField, subIndex) => (
                          <Box
                            key={subIndex}
                            sx={{
                              mb: 2,
                              px: 2,
                              py: 1.5,
                              borderRadius: 2,
                              backgroundColor: 'background.level1',
                              boxShadow: '0 1px 2px rgba(90,120,140,0.05)',
                              border: '1.5px solid',
                              borderColor: 'divider',
                              position: 'relative',
                              '&:not(:last-child)': { mb: 2 },
                            }}
                          >
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                              <Box sx={{ flex: 1.2 }}>
                                <Typography level="body3" sx={{ fontWeight: 500, mb: 0.3 }}>
                                  Name
                                </Typography>
                                <Input
                                  variant="outlined"
                                  value={subField.key}
                                  onChange={(e) => handleSubKeyChange(index, subIndex, e.target.value)}
                                  size="sm"
                                  placeholder="Subfield name"
                                  sx={{
                                    width: '100%',
                                    background: 'transparent',
                                    fontWeight: 500,
                                  }}
                                />
                              </Box>
                              <Box sx={{ flex: 2 }}>
                                <Typography level="body3" sx={{ fontWeight: 500, mb: 0.3 }}>
                                  Value
                                </Typography>
                                <Textarea
                                  value={subField.value}
                                  onChange={(e) => handleSubValueChange(index, subIndex, e.target.value)}
                                  placeholder="Value (optional)"
                                  minRows={2}
                                  size="sm"
                                  sx={{
                                    width: '100%',
                                    fontSize: 15,
                                    borderRadius: 2,
                                    border: '1.5px solid',
                                    borderColor: 'divider',
                                    fontFamily: 'inherit',
                                    background: 'transparent',
                                    resize: 'vertical',
                                  }}
                                />
                              </Box>
                              <Tooltip title="Delete subfield">
                                <IconButton
                                  onClick={() => handleDeleteSubField(index, subIndex)}
                                  size="sm"
                                  color="danger"
                                  sx={{
                                    mt: 3.5,
                                    ml: 1,
                                    alignSelf: 'flex-start',
                                    '&:hover': { backgroundColor: 'danger.softBg' },
                                  }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        ))}
                        <Button
                          onClick={() => handleAddSubField(index)}
                          startDecorator={<AddIcon />}
                          size="sm"
                          variant="plain"
                          sx={{
                            color: 'primary.600',
                            fontWeight: 500,
                            px: 1.5,
                            mt: 0.5,
                            borderRadius: 1,
                            width: 'fit-content',
                            ml: 1,
                          }}
                        >
                          Add subfield
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ mt: 0.7, ml: 5.6 }}>
                      <Textarea
                        value={field.value}
                        onChange={(e) => handleValueChange(index, e.target.value)}
                        placeholder={field.type === 'date' ? 'YYYY-MM-DD' : 'Example value (optional)'}
                        minRows={2}
                        size="sm"
                        sx={{
                          width: '65%',
                          fontSize: 15,
                          borderRadius: 2,
                          border: '1.5px solid',
                          borderColor: 'divider',
                          background: 'transparent',
                          fontFamily: 'inherit',
                          resize: 'vertical',
                        }}
                      />
                    </Box>
                  )}
                </Card>
              );
            })}
          </Box>
          {/* Action Buttons pinned bottom */}
          <Box
            sx={{
              mt: 'auto',
              pt: 1.5,
              pb: 0.5,
              display: 'flex',
              justifyContent: 'space-between',
              background: 'transparent',
              alignItems: 'center',
            }}
          >
            <Button
              onClick={handleAddField}
              startDecorator={<AddIcon />}
              size="md"
              variant="soft"
              color="primary"
              sx={{ minWidth: 130, borderRadius: 'md', fontWeight: 500 }}
            >
              Add Field
            </Button>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                onClick={handleSave}
                size="md"
                color="primary"
                variant="solid"
                disabled={!dirty}
                sx={{ minWidth: 110, borderRadius: 'md', fontWeight: 600 }}
              >
                Save
              </Button>
              <Button
                onClick={handleClose}
                size="md"
                color="neutral"
                variant="outlined"
                sx={{ minWidth: 110, borderRadius: 'md', fontWeight: 500 }}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Box>
        {/* No preview panel */}
      </Box>
      {/* Confirm Discard Modal */}
      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <ModalDialog>
          <DialogTitle>Discard unsaved changes?</DialogTitle>
          <DialogContent>
            You have unsaved changes. Are you sure you want to close without saving?
          </DialogContent>
          <DialogActions>
            <Button variant="plain" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button
              variant="plain"
              color="danger"
              onClick={() => {
                setConfirmOpen(false);
                setDirty(false);
                if (onClose) onClose();
              }}
            >
              Discard
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </>
  );
};

export default JsonStructureEditor;
